import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Check, Sparkles, Wallet } from 'lucide-react';
import { cardsAPI, walletAPI } from '../services/api';
import { formatCurrency, CRYPTO_SYMBOLS, CRYPTO_NAMES } from '../utils/constants';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';

const Cards = () => {
  const [availableCards, setAvailableCards] = useState([]);
  const [myCards, setMyCards] = useState([]);
  const [aedBalance, setAedBalance] = useState(0);
  const [cryptoBalances, setCryptoBalances] = useState([]);
  const [purchasing, setPurchasing] = useState(null);
  const [cryptoPayDialog, setCryptoPayDialog] = useState({ open: false, tier: null, price: 0 });
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const navigate = useNavigate();

  // Crypto rates (AED per 1 unit of crypto)
  const CRYPTO_RATES = {
    'BTC': 350000, 'ETH': 13000, 'USDT': 3.67, 'USDC': 3.67, 'BNB': 2200, 'SOL': 700
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [cardsRes, myCardsRes, balancesRes] = await Promise.all([
        cardsAPI.getAvailable(),
        cardsAPI.getMyCards(),
        walletAPI.getBalances()
      ]);
      setAvailableCards(cardsRes.data);
      setMyCards(myCardsRes.data);
      const aed = balancesRes.data.find(b => b.crypto === 'AED');
      setAedBalance(aed?.balance || 0);
      setCryptoBalances(balancesRes.data.filter(b => b.crypto !== 'AED'));
    } catch (error) {
      toast.error('Failed to load card data');
    }
  };

  const handlePurchaseWithAED = async (tier) => {
    setPurchasing(tier);
    try {
      const response = await cardsAPI.purchase({ tier });
      toast.success(`${tier.charAt(0).toUpperCase() + tier.slice(1)} card purchased successfully!`);
      navigate(`/card/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  const handlePurchaseWithCrypto = async () => {
    const { tier } = cryptoPayDialog;
    setPurchasing(tier);
    try {
      const response = await cardsAPI.purchaseWithCrypto({ tier, crypto: selectedCrypto });
      toast.success(`${tier.charAt(0).toUpperCase() + tier.slice(1)} card purchased with ${selectedCrypto}!`);
      setCryptoPayDialog({ open: false, tier: null, price: 0 });
      navigate(`/card/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  const openCryptoPayDialog = (tier, price) => {
    setCryptoPayDialog({ open: true, tier, price });
    setSelectedCrypto('BTC');
  };

  const getCryptoAmount = (priceAED, crypto) => {
    const rate = CRYPTO_RATES[crypto] || 1;
    return priceAED / rate;
  };

  const getCryptoBalance = (crypto) => {
    const balance = cryptoBalances.find(b => b.crypto === crypto);
    return balance?.balance || 0;
  };

  const canAffordWithCrypto = (priceAED, crypto) => {
    const required = getCryptoAmount(priceAED, crypto);
    const available = getCryptoBalance(crypto);
    return available >= required;
  };

  return (
    <Layout>
      <div className="space-y-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-primary font-bold text-white mb-2">PAYMENT CARDS</h1>
          <p className="text-gray-400 font-secondary">Choose your premium card</p>
          <div className="mt-4 flex flex-wrap gap-4">
            <div className="bg-white/5 border border-white/10 rounded-full px-6 py-3">
              <span className="text-gray-400 font-secondary text-sm">AED Balance: </span>
              <span className="text-[#00F0FF] font-mono font-bold">{formatCurrency(aedBalance)}</span>
            </div>
            {cryptoBalances.filter(b => b.balance > 0).slice(0, 3).map(b => (
              <div key={b.crypto} className="bg-white/5 border border-white/10 rounded-full px-4 py-3">
                <span className="text-gray-400 font-secondary text-sm">{CRYPTO_SYMBOLS[b.crypto]} </span>
                <span className="text-white font-mono">{b.balance.toFixed(4)}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* My Cards */}
        {myCards.length > 0 && (
          <div>
            <h2 className="text-2xl font-primary font-bold text-white mb-6">MY CARDS</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myCards.map((card, index) => (
                <motion.div
                  key={card.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  onClick={() => navigate(`/card/${card.id}`)}
                  className="cursor-pointer"
                  data-testid={`my-card-${card.tier}`}
                >
                  <div className={`relative h-48 rounded-2xl p-6 overflow-hidden ${
                    card.tier === 'silver' ? 'gradient-card-silver' : 'gradient-card-gold'
                  }`}>
                    <div className="relative z-10 h-full flex flex-col justify-between">
                      <div className="flex items-center justify-between">
                        <CreditCard className="w-8 h-8 text-black/70" />
                        <span className="text-black/70 font-primary font-bold uppercase text-sm">{card.tier}</span>
                      </div>
                      <div>
                        <div className="text-black/70 font-mono text-lg tracking-wider">•••• {card.card_number.slice(-4)}</div>
                        <div className="text-black/70 font-secondary text-sm mt-1">{card.holder_name}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Available Cards */}
        <div>
          <h2 className="text-2xl font-primary font-bold text-white mb-6">{myCards.length > 0 ? 'GET ANOTHER CARD' : 'AVAILABLE CARDS'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {availableCards.map((card, index) => {
              const alreadyOwned = myCards.some(c => c.tier === card.tier);
              return (
                <motion.div
                  key={card.tier}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-card p-8 rounded-2xl hover:border-[#7000FF]/50 transition-all duration-300"
                  data-testid={`card-tier-${card.tier}`}
                >
                  {/* Card Preview */}
                  <div className={`relative h-52 rounded-2xl mb-6 p-6 overflow-hidden ${
                    card.tier === 'silver' ? 'gradient-card-silver' : 'gradient-card-gold'
                  }`}>
                    <div className="relative z-10 h-full flex flex-col justify-between">
                      <div className="flex items-center justify-between">
                        <CreditCard className="w-10 h-10 text-black/70" />
                        <Sparkles className="w-6 h-6 text-black/70" />
                      </div>
                      <div>
                        <div className="text-black/70 font-mono text-xl tracking-wider">•••• •••• •••• 1234</div>
                        <div className="text-black/70 font-secondary mt-2 uppercase font-bold text-lg">{card.name}</div>
                      </div>
                    </div>
                  </div>

                  {/* Card Info */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-primary font-bold text-white uppercase">{card.name}</h3>
                      <div className="text-[#00F0FF] font-mono font-bold text-2xl">{formatCurrency(card.price)}</div>
                    </div>
                    <ul className="space-y-3">
                      {card.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-gray-300 font-secondary">
                          <Check className="w-5 h-5 text-[#00FF94] flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Purchase Buttons */}
                  {alreadyOwned ? (
                    <div className="text-center py-3 text-[#00FF94] font-secondary">Already Owned</div>
                  ) : (
                    <div className="space-y-3">
                      {/* Pay with AED Balance */}
                      {aedBalance >= card.price && (
                        <Button
                          onClick={() => handlePurchaseWithAED(card.tier)}
                          disabled={purchasing === card.tier}
                          className={`w-full rounded-full py-6 text-lg font-bold ${
                            card.tier === 'silver'
                              ? 'bg-[#C0C0C0] hover:bg-[#A0A0A0] text-black'
                              : 'bg-[#FFD700] hover:bg-[#FFC700] text-black'
                          }`}
                          data-testid={`purchase-aed-${card.tier}-btn`}
                        >
                          {purchasing === card.tier ? 'Processing...' : `Pay with AED Balance`}
                        </Button>
                      )}
                      
                      {/* Pay with Crypto */}
                      <Button
                        onClick={() => openCryptoPayDialog(card.tier, card.price)}
                        disabled={purchasing === card.tier}
                        className="w-full bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full py-6 text-lg font-bold neon-glow-primary flex items-center justify-center gap-2"
                        data-testid={`purchase-crypto-${card.tier}-btn`}
                      >
                        <Wallet className="w-5 h-5" />
                        Pay with Crypto
                      </Button>
                      
                      {aedBalance < card.price && cryptoBalances.every(b => !canAffordWithCrypto(card.price, b.crypto)) && (
                        <p className="text-gray-400 text-sm text-center">
                          Insufficient balance. Deposit crypto first.
                        </p>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Crypto Payment Dialog */}
      <Dialog open={cryptoPayDialog.open} onOpenChange={(open) => setCryptoPayDialog({ ...cryptoPayDialog, open })}>
        <DialogContent className="bg-[#0A051E] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-primary text-2xl">PAY WITH CRYPTO</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
              <p className="text-gray-400 text-sm">Card Price</p>
              <p className="text-3xl font-mono font-bold text-[#00F0FF]">{formatCurrency(cryptoPayDialog.price)}</p>
            </div>

            <div>
              <Label className="text-gray-300 mb-2 block">Select Cryptocurrency</Label>
              <Select value={selectedCrypto} onValueChange={setSelectedCrypto}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="crypto-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A051E] border-white/10">
                  {['BTC', 'ETH', 'USDT', 'USDC', 'BNB', 'SOL'].map(crypto => (
                    <SelectItem key={crypto} value={crypto} className="text-white hover:bg-white/10">
                      <div className="flex items-center justify-between w-full">
                        <span>{CRYPTO_SYMBOLS[crypto]} {CRYPTO_NAMES[crypto]}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">Amount to pay:</span>
                <span className="text-white font-mono font-bold">
                  {getCryptoAmount(cryptoPayDialog.price, selectedCrypto).toFixed(8)} {selectedCrypto}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Your balance:</span>
                <span className={`font-mono ${canAffordWithCrypto(cryptoPayDialog.price, selectedCrypto) ? 'text-[#00FF94]' : 'text-red-500'}`}>
                  {getCryptoBalance(selectedCrypto).toFixed(8)} {selectedCrypto}
                </span>
              </div>
            </div>

            {!canAffordWithCrypto(cryptoPayDialog.price, selectedCrypto) && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-sm text-red-400">
                Insufficient {selectedCrypto} balance. Please deposit more or select another cryptocurrency.
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => setCryptoPayDialog({ open: false, tier: null, price: 0 })}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-full py-4"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePurchaseWithCrypto}
                disabled={purchasing || !canAffordWithCrypto(cryptoPayDialog.price, selectedCrypto)}
                className="flex-1 bg-[#00FF94] hover:bg-[#00CC77] text-black rounded-full py-4 font-bold"
                data-testid="confirm-crypto-purchase-btn"
              >
                {purchasing ? 'Processing...' : 'Confirm Purchase'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Cards;
